import { useContext, useEffect, useState } from "react"

import { ChevronLeftIcon } from "@chakra-ui/icons"
import { Button, Stack, Text } from "@chakra-ui/react"
import { COLLEGE_NESTED_FIELDS_TO_PLAIN_OBJECT, CollegeName } from "data"
import { env } from "env"
import { Navigate, useNavigate } from "react-router-dom"
import { useWatch } from "react-hook-form"
import { sendTealiumEvent } from "tealium"
import { ApplicationData, ContactInfo, FieldMetaData, SectionData } from "types"

import { AccordionCardStack, ExternalLink, H1, Span } from "components/common"
import { DebugSubmissionModal } from "components/DebugSubmissionModal"
import { FinalCheckCard } from "components/FinalCheckCard"
import { PageHeader } from "components/LayoutComponents"

import { ClientContext } from "context/ClientContext"
import FormContext from "context/FormContext"
import { UserContext } from "context/UserContext"

import { AppRoutes } from "utilities/constants"
import { dehyphenate } from "utilities/formatters"
import {
  getInvalidSections,
  getSectionMetaData,
  transformDataToServerFormat,
} from "utilities/helpers"

interface FinalCheckPageProps {
  applicationData: ApplicationData
}

export const FinalCheckPage = ({ applicationData }: FinalCheckPageProps) => {
  const {
    onSave,
    onSubmit,
    form: { formState, getValues, control },
  } = useContext(FormContext)
  const { user, setUser } = useContext(UserContext)
  const { client } = useContext(ClientContext)
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmissionFailed, setHasSubmissionFailed] = useState(false)
  const [showDebugModal, setShowDebugModal] = useState(false)

  const [initialInvalidSections, setInitialInvalidSections] = useState<
    FieldMetaData[]
  >([])
  const [invalidSections, setInvalidSections] = useState<Map<string, string[]>>(
    new Map(),
  )

  const [redirectToFirstPage, setRedirectToFirstPage] = useState(false)

  // Watch all form values. Changes may trigger changes to dependent fields/sections
  const allFormValues = useWatch({ control })

  // Send tealium event on page load
  useEffect(() => {
    sendTealiumEvent("app_entered_validation", {
      type: "link",
      page: "final_check_page",
      email_address: user?.email,
    })
  }, [user])

  // Update invalid sections when form values or errors change
  useEffect(() => {
    const currentValues = getValues()
    const arrayToCheck =
      COLLEGE_NESTED_FIELDS_TO_PLAIN_OBJECT[
        // `theme` === the college name (e.g., "siena").
        client?.theme as keyof typeof COLLEGE_NESTED_FIELDS_TO_PLAIN_OBJECT
      ]

    // Clone before transforming: transformDataToServerFormat mutates in place and
    // deletes nested arrays (e.g. parents). Nested validation still reads those
    // arrays from currentValues, so they must remain intact.
    const formValues = transformDataToServerFormat(
      structuredClone(currentValues),
      arrayToCheck,
    )

    const invalidSections = getInvalidSections({
      applicationData: applicationData,
      formValues: formValues ?? {},
      errors: formState.errors,
      currentValues,
    })

    const invalidSectionsMetaData = invalidSections.map(section => {
      return getSectionMetaData(applicationData, section)
    })

    setInitialInvalidSections(data => {
      const existingTitles = new Set(
        data.map(page => page.section?.title ?? page.section?.nestedSectionTitle ?? "")
      )
      const newSections = invalidSectionsMetaData.filter(page => {
        const title = page.section?.title ?? page.section?.nestedSectionTitle ?? ""
        return !existingTitles.has(title)
      })
      return [...data, ...newSections]
    })
    // eslint-disable-next-line
  }, [applicationData, formState.errors, allFormValues])

  const updateSectionErrors = (
    sectionTitle: string,
    sectionErrors: string[],
  ) => {
    // N.B: This has to be functional setState since each card calls it at the same time on load
    setInvalidSections(prevState => {
      const currentErrors = prevState.get(sectionTitle)

      // Prevent re-renders if the errors haven't changed.
      if (
        currentErrors &&
        currentErrors.length === sectionErrors.length &&
        JSON.stringify(currentErrors) === JSON.stringify(sectionErrors)
      ) {
        return prevState
      }

      const newInvalidSections = new Map(prevState)
      newInvalidSections.set(sectionTitle, sectionErrors)
      return newInvalidSections
    })
  }

  const saveData = async () => {
    const user = await onSave()
    if (user) {
      setUser(user)
      return user
    }
    throw new Error("No user found")
  }

  const handleAppSubmissionClicked = () => {
    if (!env.DEV) {
      handleSubmitApplication()
    } else {
      handleDebugAppCompletion()
    }
  }

  const handleBackButtonClicked = () => {
    saveData()
    setRedirectToFirstPage(true)
  }

  const handleDebugAppCompletion = () => {
    setShowDebugModal(true)
  }

  const handleSubmitApplication = async () => {
    setIsSubmitting(true)

    try {
      await saveData()
      const submittedUser = await onSubmit()

      if (submittedUser && submittedUser?.submittedAt) {
        // Update the user context first
        setUser(submittedUser)
        // Then navigate
        navigate(AppRoutes.confirmation, { replace: true })
      } else {
        setHasSubmissionFailed(true)
      }
    } catch (err) {
      console.error("Error during handleSubmitApplication", err)
      setHasSubmissionFailed(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const errorsArePresent = Array.from(invalidSections.values()).some(
    sectionErrors => sectionErrors.length > 0,
  )

  if (redirectToFirstPage) {
    return <Navigate to={`/${applicationData.pages[0].slug}`} replace />
  }

  return (
    <>
      <PageHeader textAlign="center" className="final-check">
        {hasSubmissionFailed ? (
          <SubmissionFailureMessage contact={applicationData.meta.contact} />
        ) : errorsArePresent ? (
          <AppIncompleteMessage />
        ) : (
          <AppCompleteMessage />
        )}
      </PageHeader>
      {!!initialInvalidSections.length && (
        <AccordionCardStack
          allowMultiple={false}
          allowToggle
          spacing={{ base: 0, md: 8, lg: 12 }}
          marginBottom={{ base: 8, md: 8, lg: 12 }}
        >
          {initialInvalidSections.map(({ page, pageNumber, section }) => {
            if (!page || !section || !client) return null
            const sectionTitle =
              section.title ?? section.nestedSectionTitle ?? ""

            // Properly type the section data
            const sectionData: SectionData = {
              ...section,
              fields: section.fields || [],
            }
            return (
              <FinalCheckCard
                key={`p${page.id}_${sectionTitle}`}
                pageNumber={pageNumber ?? 0}
                pageSlug={page.slug ?? ""}
                pageTitle={page.title || ""}
                sectionData={sectionData}
                sectionErrors={invalidSections.get(sectionTitle) || []}
                updateSectionErrors={updateSectionErrors}
                client={client?.theme as CollegeName}
              />
            )
          })}
        </AccordionCardStack>
      )}

      <Stack
        alignItems="center"
        marginBottom={{ base: 8, md: 12 }}
        paddingX={{ base: 4, md: 0 }}
        spacing={{ base: 6, md: 8 }}
        sx={{ "& > button": { whiteSpace: "break-spaces" } }}
      >
        <Button
          onClick={handleAppSubmissionClicked}
          isDisabled={errorsArePresent}
          isLoading={isSubmitting}
          loadingText="Submitting..."
          className="final-check__submit-button"
          size="lg"
          title={
            errorsArePresent
              ? "All form errors must be fixed before submission"
              : undefined
          }
        >
          Submit Application to {applicationData.meta.name}
        </Button>
        <Button
          id="double-check"
          onClick={handleBackButtonClicked}
          size="lg"
          variant="link"
        >
          <ChevronLeftIcon mr={2} />I want to double check everything
        </Button>
      </Stack>
      {showDebugModal && (
        <DebugSubmissionModal
          setIsSubmitting={setIsSubmitting}
          handleSubmitApplication={handleSubmitApplication}
          setShowDebugModal={setShowDebugModal}
        />
      )}
    </>
  )
}

const AppCompleteMessage = () => {
  return (
    <>
      <H1 mb="4">Ready to apply?</H1>
      <Text>
        Great job! Your application is complete. Submit it below when you're
        ready.
      </Text>
    </>
  )
}

const AppIncompleteMessage = () => {
  return (
    <>
      <H1 mb="4">Almost Done</H1>
      <Text>
        You have a few items to review before you can submit your application.
      </Text>
    </>
  )
}

const SubmissionFailureMessage = ({ contact }: { contact?: ContactInfo }) => {
  return (
    <>
      <H1 mb="4">Hmm, something's gone wrong.</H1>
      <Text>
        Your application is complete but has not been submitted successfully.
        Please try again.
        {contact && (
          <Text>
            If this problem continues, please contact us at{" "}
            <ExternalLink
              href={`mailto:${contact.email}`}
              children={contact.email}
              color="buttonLinkColor"
            />
            {contact.phone && (
              <>
                {" "}
                or{" "}
                <ExternalLink
                  href={`tel:${dehyphenate(contact.phone)}`}
                  children={contact.phone}
                  color="buttonLinkColor"
                />
              </>
            )}
            <Span ml={-1}>.</Span>
          </Text>
        )}
      </Text>
    </>
  )
}
